import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { isGrpcRuntimeAvailable } from '../../../shared/api/grpc/transport';
import { saveCurrentConfigurationToPipeex, SettingsType } from '../../../shared/api/pipeex/pcfSettingsClient';
import { sanitizeAllObjectTypeAttributes } from '../../../shared/lib/utils/mappingConfigurationSanitizer';
import type { Attribute, ObjectType } from '../../../shared/types';
import type { MappingConfiguration } from '../../../shared/types/config/mapping.types';

const normalizePcfAttributeKey = (value: string): string =>
  value.trim().toUpperCase().replace(/[\s_-]/g, '');

type RequiredAllAttributeSeed = {
  pcfAttributeName: string;
  revitParam: string;
  attributeType: 'Parameter' | 'BomColumn';
};

export const useImprovedMappingConfiguration = () => {
  // Get all necessary state from Zustand
  const {
    // Mapping configuration state
    general,
    advanced,
  } = useConfigurationStore(state => state.mappingConfiguration);

  // Get state from other configurations
  const {
    processConditionsValue,
    projectPropertiesValue,
    pipingSpecValuePC,
    tracingSpecValuePC,
    attribute12Value,
    attribute13Value,
    attribute14Value,
    attribute15Value,
    attribute16Value,
    attribute17Value,
    attribute18Value,
    attribute19Value,
    attribute20Value,
    attribute21Value,
    attribute22Value,
    attribute23Value,
    projectIdentifierValue,
    projectNameValue,
    areaValue,
    attribute1ClientName,
    attribute2ProjectAddress,
    attribute3ClientDepartment,
    attribute4ClientProjectNumber,
    attribute5ClientDocumentNumber,
    attribute6ClientCode,
    attribute7EngPartyOffice,
    attribute8EngPartyOfficeLocation,
    attribute9EngPartyProjectNumber,
    attribute10EngPartyAreaCode,
    processConditionsChecked,
    projectPropertiesChecked
  } = useConfigurationStore(state => state.processPropertiesConfiguration);

  const {
    endpoint1Value,
    endpoint2Value,
    endpoint3Value,
    endpoint4Value,
    materialValue,
    designStdValue,
    pressureClassValue,
    insulationSpecValue,
    insulationThicknessValue,
    weightValue,
    tracingSpecValue,
    itemDescriptionValue
  } = useConfigurationStore(state => state.connectorBindingConfiguration);

  const {
    objectTypeValue,
    sKeyValue,
    pipingSpecValue,
    systemGroupingValue
  } = useConfigurationStore(state => state.menuConfiguration);

  // Get Zustand actions
  const updateMappingAttribute = useConfigurationStore(state => state.updateMappingAttribute);
  const addMappingAttribute = useConfigurationStore(state => state.addMappingAttribute);
  const removeMappingAttribute = useConfigurationStore(state => state.removeMappingAttribute);
  const addObjectType = useConfigurationStore(state => state.addObjectType);
  const updateAdvancedSettings = useConfigurationStore(state => state.updateAdvancedSettings);
  const updateProcessPropertiesField = useConfigurationStore(state => state.updateProcessPropertiesField);
  const updateMenuConfiguration = useConfigurationStore(state => state.updateMenuConfiguration);
  const updateConnectorBindingField = useConfigurationStore(state => state.updateConnectorBindingField);

  // State for tabs
  const [activeTab, setActiveTab] = useState(0);

  // State for mapping configuration - Use the Zustand state directly
  const [generalSettings, setGeneralSettings] = useState<MappingConfiguration['general']>(() => general);
  const [advancedSettings, setAdvancedSettings] = useState<MappingConfiguration['advanced']>(() => advanced);

  // State for selected object type
  const [selectedObjectType, setSelectedObjectType] = useState("ALL");

  // State for collapsible sections
  const [processConditionsExpanded, setProcessConditionsExpanded] = useState(false);
  const [projectPropertiesExpanded, setProjectPropertiesExpanded] = useState(false);
  const requiredAllAttributeSourceValuesRef = useRef<Record<string, string>>({});

  const syncGeneralSettingsToStore = useCallback((nextGeneralSettings: MappingConfiguration['general'], markDirty = true) => {
    useConfigurationStore.setState((state) => ({
      mappingConfiguration: {
        ...state.mappingConfiguration,
        general: nextGeneralSettings,
      },
      isDirty: markDirty || state.isDirty,
    }));
  }, []);

  const syncAdvancedSettingsToStore = useCallback((nextAdvancedSettings: MappingConfiguration['advanced']) => {
    useConfigurationStore.setState((state) => ({
      mappingConfiguration: {
        ...state.mappingConfiguration,
        advanced: nextAdvancedSettings,
      },
      isDirty: true,
    }));
  }, []);

  useEffect(() => {
    setGeneralSettings(general);
  }, [general]);

  useEffect(() => {
    setAdvancedSettings(advanced);
  }, [advanced]);

  // Sync configurations from previous pages to the ALL section's attributes.
  // Match by PCF attribute name (not row order) to avoid overwriting wrong rows
  // when users reorder/add attributes.
  useEffect(() => {
    const allObjectTypeIndex = generalSettings.mappingTable.objectType.findIndex(
      (objType) => objType.name === 'ALL'
    );
    if (allObjectTypeIndex === -1) {
      console.warn('[useImprovedMappingConfiguration] ALL object type not found');
      return;
    }

    const updatedObjectTypes = [...generalSettings.mappingTable.objectType];
    const allObjectType = { ...updatedObjectTypes[allObjectTypeIndex] };
    const attributes = [...allObjectType.attributes];

    const requiredAllAttributes: RequiredAllAttributeSeed[] = [
      { pcfAttributeName: 'END-POINT_1', revitParam: endpoint1Value?.value || '', attributeType: 'Parameter' },
      { pcfAttributeName: 'END-POINT_2', revitParam: endpoint2Value?.value || '', attributeType: 'Parameter' },
      { pcfAttributeName: 'END-POINT_3', revitParam: endpoint3Value?.value || '', attributeType: 'Parameter' },
      { pcfAttributeName: 'END-POINT_4', revitParam: endpoint4Value?.value || '', attributeType: 'Parameter' },
      { pcfAttributeName: 'PressureClass', revitParam: pressureClassValue?.value || '', attributeType: 'BomColumn' },
      { pcfAttributeName: 'Material', revitParam: materialValue?.value || '', attributeType: 'BomColumn' },
      { pcfAttributeName: 'DesignStd', revitParam: designStdValue?.value || '', attributeType: 'BomColumn' },
      { pcfAttributeName: 'ITEM-DESCRIPTION', revitParam: itemDescriptionValue?.value || '', attributeType: 'Parameter' },
    ];
    const previousSourceValues = requiredAllAttributeSourceValuesRef.current;
    const nextSourceValues = requiredAllAttributes.reduce<Record<string, string>>(
      (accumulator, attribute) => {
        accumulator[normalizePcfAttributeKey(attribute.pcfAttributeName)] = attribute.revitParam;
        return accumulator;
      },
      {}
    );
    requiredAllAttributeSourceValuesRef.current = nextSourceValues;

    let updateCount = 0;
    let addCount = 0;
    let nextOrder = attributes.reduce((max, attr) => Math.max(max, Number(attr.order || 0)), 0) + 1;
    const updatedAttributes = [...attributes];

    requiredAllAttributes.forEach((requiredAttr) => {
      const normalizedRequiredName = normalizePcfAttributeKey(requiredAttr.pcfAttributeName);
      const existingIndex = updatedAttributes.findIndex(
        (attr) => normalizePcfAttributeKey(attr.pcfAttributeName || '') === normalizedRequiredName
      );

      if (existingIndex >= 0) {
        const existing = updatedAttributes[existingIndex];
        const existingRevitParam = existing.revitParam ?? '';
        const previousSourceValue = previousSourceValues[normalizedRequiredName];
        const sourceChanged =
          previousSourceValue !== undefined && previousSourceValue !== requiredAttr.revitParam;
        const stillMatchesPreviousSource = existingRevitParam === previousSourceValue;
        const shouldSeedEmptyRow =
          existingRevitParam.trim() === '' && requiredAttr.revitParam.trim() !== '';
        const needsUpdate =
          shouldSeedEmptyRow || (sourceChanged && stillMatchesPreviousSource);

        if (needsUpdate) {
          updateCount++;
          updatedAttributes[existingIndex] = {
            ...existing,
            revitParam: requiredAttr.revitParam,
          };
        }
        return;
      }

      addCount++;
      updatedAttributes.push({
        order: nextOrder++,
        pcfAttributeName: requiredAttr.pcfAttributeName,
        revitParam: requiredAttr.revitParam,
        attributeType: requiredAttr.attributeType,
        fx: '',
      });
    });

    if (updateCount === 0 && addCount === 0) {
      return;
    }

    allObjectType.attributes = updatedAttributes;
    updatedObjectTypes[allObjectTypeIndex] = allObjectType;

    const nextGeneralSettings = {
      ...generalSettings,
      mappingTable: {
        ...generalSettings.mappingTable,
        objectType: updatedObjectTypes
      }
    };

    setGeneralSettings(nextGeneralSettings);
    syncGeneralSettingsToStore(nextGeneralSettings, false);
  }, [
    generalSettings,
    endpoint1Value?.value,
    endpoint2Value?.value,
    endpoint3Value?.value,
    endpoint4Value?.value,
    materialValue?.value,
    designStdValue?.value,
    pressureClassValue?.value,
    itemDescriptionValue?.value,
    syncGeneralSettingsToStore,
  ]);

  useEffect(() => {
    const allObjectTypeIndex = generalSettings.mappingTable.objectType.findIndex(
      (objType) => objType.name === 'ALL'
    );
    if (allObjectTypeIndex === -1) {
      return;
    }

    const allObjectType = generalSettings.mappingTable.objectType[allObjectTypeIndex];
    const sanitizedAttributes = sanitizeAllObjectTypeAttributes(allObjectType.attributes || []);
    if (sanitizedAttributes.length === allObjectType.attributes.length) {
      return;
    }

    const updatedObjectTypes = [...generalSettings.mappingTable.objectType];
    updatedObjectTypes[allObjectTypeIndex] = {
      ...allObjectType,
      attributes: sanitizedAttributes,
    };

    const nextGeneralSettings = {
      ...generalSettings,
      mappingTable: {
        ...generalSettings.mappingTable,
        objectType: updatedObjectTypes,
      },
    };

    setGeneralSettings(nextGeneralSettings);
    syncGeneralSettingsToStore(nextGeneralSettings, false);
  }, [generalSettings, syncGeneralSettingsToStore]);

  // Handle tab change
  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  }, []);

  // Handle object type change
  const handleObjectTypeChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedObjectType(event.target.value);
  }, []);

  // Flow pages are the source of truth.
  // Mapping-table edits must not push values back to earlier workflow stores.
  const syncAttributeChangeToSourceStores = useCallback((_pcfAttributeName: string, _revitParamValue: string) => {
    // Intentionally no-op.
  }, []);

  const handleAttributeChange = useCallback((order: number, field: keyof Attribute, value: string) => {
    const objectTypeIndex = generalSettings.mappingTable.objectType.findIndex(
      (objType) => objType.name === selectedObjectType
    );
    if (objectTypeIndex === -1) {
      return;
    }

    const attributeIndex = generalSettings.mappingTable.objectType[objectTypeIndex].attributes.findIndex(
      (attr) => attr.order === order
    );
    if (attributeIndex === -1) {
      return;
    }

    const currentAttribute = generalSettings.mappingTable.objectType[objectTypeIndex].attributes[attributeIndex];
    if (currentAttribute[field] === value) {
      return;
    }

    setGeneralSettings(prev => {
      const nextObjectTypeIndex = prev.mappingTable.objectType.findIndex(objType => objType.name === selectedObjectType);
      if (nextObjectTypeIndex === -1) return prev;

      const nextAttributeIndex = prev.mappingTable.objectType[nextObjectTypeIndex].attributes.findIndex(attr => attr.order === order);
      if (nextAttributeIndex === -1) return prev;

      if (prev.mappingTable.objectType[nextObjectTypeIndex].attributes[nextAttributeIndex][field] === value) {
        return prev;
      }

      // Update the local state
      const updatedObjectTypes = [...prev.mappingTable.objectType];
      const updatedAttributes = [...updatedObjectTypes[nextObjectTypeIndex].attributes];
      updatedAttributes[nextAttributeIndex] = { ...updatedAttributes[nextAttributeIndex], [field]: value };
      updatedObjectTypes[nextObjectTypeIndex] = { ...updatedObjectTypes[nextObjectTypeIndex], attributes: updatedAttributes };

      const newGeneralSettings = {
        ...prev,
        mappingTable: {
          ...prev.mappingTable,
          objectType: updatedObjectTypes
        }
      };

      return newGeneralSettings;
    });

    // Update Zustand store - ALL attributes are persisted here (including custom ones)
    updateMappingAttribute(selectedObjectType, attributeIndex, { [field]: value });

    // Intentionally no-op to preserve workflow precedence.
    if (field === 'revitParam') {
      syncAttributeChangeToSourceStores(currentAttribute.pcfAttributeName, value);
    }
  }, [generalSettings, selectedObjectType, updateMappingAttribute, syncAttributeChangeToSourceStores]);

  // Add new attribute
  const handleAddAttribute = useCallback(() => {
    const objectTypeIndex = generalSettings.mappingTable.objectType.findIndex(
      (objType) => objType.name === selectedObjectType
    );
    if (objectTypeIndex === -1) {
      return;
    }

    const maxOrder = Math.max(
      ...generalSettings.mappingTable.objectType[objectTypeIndex].attributes.map((attr) => attr.order),
      0
    );

    const newAttribute: Attribute = {
      order: maxOrder + 1,
      pcfAttributeName: "New Attribute",
      revitParam: "",
      attributeType: "Parameter",
      fx: "",
      isCustom: true
    };

    setGeneralSettings(prev => {
      const nextObjectTypeIndex = prev.mappingTable.objectType.findIndex(objType => objType.name === selectedObjectType);
      if (nextObjectTypeIndex === -1) return prev;

      // Update the local state
      const updatedObjectTypes = [...prev.mappingTable.objectType];
      updatedObjectTypes[nextObjectTypeIndex] = {
        ...updatedObjectTypes[nextObjectTypeIndex],
        attributes: [...updatedObjectTypes[nextObjectTypeIndex].attributes, newAttribute]
      };

      const newGeneralSettings = {
        ...prev,
        mappingTable: {
          ...prev.mappingTable,
          objectType: updatedObjectTypes
        }
      };

      return newGeneralSettings;
    });

    // Update Zustand store
    addMappingAttribute(selectedObjectType, newAttribute);
  }, [generalSettings.mappingTable.objectType, selectedObjectType, addMappingAttribute]);

  // Remove attribute
  const handleRemoveAttribute = useCallback((order: number) => {
    const objectTypeIndex = generalSettings.mappingTable.objectType.findIndex(
      (objType) => objType.name === selectedObjectType
    );
    if (objectTypeIndex === -1) {
      return;
    }

    const attributeIndex = generalSettings.mappingTable.objectType[objectTypeIndex].attributes.findIndex(
      (attr) => attr.order === order
    );
    if (attributeIndex === -1) {
      return;
    }

    setGeneralSettings(prev => {
      const nextObjectTypeIndex = prev.mappingTable.objectType.findIndex(objType => objType.name === selectedObjectType);
      if (nextObjectTypeIndex === -1) return prev;

      const nextAttributeIndex = prev.mappingTable.objectType[nextObjectTypeIndex].attributes.findIndex(attr => attr.order === order);
      if (nextAttributeIndex === -1) return prev;

      const updatedObjectTypes = [...prev.mappingTable.objectType];
      const updatedAttributes = updatedObjectTypes[nextObjectTypeIndex].attributes.filter(attr => attr.order !== order);
      updatedObjectTypes[nextObjectTypeIndex] = { ...updatedObjectTypes[nextObjectTypeIndex], attributes: updatedAttributes };

      const newGeneralSettings = {
        ...prev,
        mappingTable: {
          ...prev.mappingTable,
          objectType: updatedObjectTypes
        }
      };

      return newGeneralSettings;
    });

    // Update Zustand store
    removeMappingAttribute(selectedObjectType, attributeIndex);
  }, [generalSettings.mappingTable.objectType, selectedObjectType, removeMappingAttribute]);

  // Add new object type
  const handleAddObjectType = useCallback(() => {
    const newObjectType: ObjectType = {
      name: `ObjectType${generalSettings.mappingTable.objectType.length}`,
      attributes: []
    };

    setGeneralSettings(prev => {
      const newGeneralSettings = {
        ...prev,
        mappingTable: {
          ...prev.mappingTable,
          objectType: [...prev.mappingTable.objectType, newObjectType]
        }
      };

      return newGeneralSettings;
    });

    // Update Zustand store
    addObjectType(newObjectType);
    setSelectedObjectType(newObjectType.name);
  }, [generalSettings.mappingTable.objectType.length, addObjectType]);

  const handleCenterOfCoordinatesChange = useCallback((value: MappingConfiguration['advanced']['centerOfCoordinates']) => {
    const newAdvancedSettings = {
      ...advancedSettings,
      centerOfCoordinates: value,
    };

    setAdvancedSettings(newAdvancedSettings);
    updateAdvancedSettings({ centerOfCoordinates: value });
    syncAdvancedSettingsToStore(newAdvancedSettings);
  }, [advancedSettings, updateAdvancedSettings, syncAdvancedSettingsToStore]);

  // Handle process conditions change
  const handleProcessConditionsChange = useCallback((checked: boolean) => {
    updateProcessPropertiesField('processConditionsChecked', checked);
  }, [updateProcessPropertiesField]);

  // Handle project properties change
  const handleProjectPropertiesChange = useCallback((checked: boolean) => {
    updateProcessPropertiesField('projectPropertiesChecked', checked);
  }, [updateProcessPropertiesField]);

  // Handle process conditions value change
  const onProcessConditionsValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('processConditionsValue', value);
  }, [updateProcessPropertiesField]);

  // Handle project properties value change
  const onProjectPropertiesValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('projectPropertiesValue', value);
  }, [updateProcessPropertiesField]);

  // Additional process condition attributes handlers
  const onPipingSpecValuePCChange = useCallback((value: string) => {
    updateProcessPropertiesField('pipingSpecValuePC', value);
  }, [updateProcessPropertiesField]);

  const onTracingSpecValuePCChange = useCallback((value: string) => {
    updateProcessPropertiesField('tracingSpecValuePC', value);
  }, [updateProcessPropertiesField]);

  const onAttribute12ValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute12Value', value);
  }, [updateProcessPropertiesField]);

  const onAttribute13ValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute13Value', value);
  }, [updateProcessPropertiesField]);

  const onAttribute14ValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute14Value', value);
  }, [updateProcessPropertiesField]);

  const onAttribute15ValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute15Value', value);
  }, [updateProcessPropertiesField]);

  const onAttribute16ValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute16Value', value);
  }, [updateProcessPropertiesField]);

  const onAttribute17ValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute17Value', value);
  }, [updateProcessPropertiesField]);

  const onAttribute18ValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute18Value', value);
  }, [updateProcessPropertiesField]);

  const onAttribute19ValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute19Value', value);
  }, [updateProcessPropertiesField]);

  const onAttribute20ValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute20Value', value);
  }, [updateProcessPropertiesField]);

  const onAttribute21ValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute21Value', value);
  }, [updateProcessPropertiesField]);

  const onAttribute22ValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute22Value', value);
  }, [updateProcessPropertiesField]);

  const onAttribute23ValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute23Value', value);
  }, [updateProcessPropertiesField]);

  // Project properties attributes handlers
  const onProjectIdentifierValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('projectIdentifierValue', value);
  }, [updateProcessPropertiesField]);

  const onProjectNameValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('projectNameValue', value);
  }, [updateProcessPropertiesField]);

  const onAreaValueChange = useCallback((value: string) => {
    updateProcessPropertiesField('areaValue', value);
  }, [updateProcessPropertiesField]);

  const onAttribute1ClientNameChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute1ClientName', value);
  }, [updateProcessPropertiesField]);

  const onAttribute2ProjectAddressChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute2ProjectAddress', value);
  }, [updateProcessPropertiesField]);

  const onAttribute3ClientDepartmentChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute3ClientDepartment', value);
  }, [updateProcessPropertiesField]);

  const onAttribute4ClientProjectNumberChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute4ClientProjectNumber', value);
  }, [updateProcessPropertiesField]);

  const onAttribute5ClientDocumentNumberChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute5ClientDocumentNumber', value);
  }, [updateProcessPropertiesField]);

  const onAttribute6ClientCodeChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute6ClientCode', value);
  }, [updateProcessPropertiesField]);

  const onAttribute7EngPartyOfficeChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute7EngPartyOffice', value);
  }, [updateProcessPropertiesField]);

  const onAttribute8EngPartyOfficeLocationChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute8EngPartyOfficeLocation', value);
  }, [updateProcessPropertiesField]);

  const onAttribute9EngPartyProjectNumberChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute9EngPartyProjectNumber', value);
  }, [updateProcessPropertiesField]);

  const onAttribute10EngPartyAreaCodeChange = useCallback((value: string) => {
    updateProcessPropertiesField('attribute10EngPartyAreaCode', value);
  }, [updateProcessPropertiesField]);

  // Handle piping system property change
  const handlePipingSystemPropertyChange = useCallback((name: string, value: string) => {
    const updatedProperties = [...generalSettings.pipingSystemProperties];
    const propertyIndex = updatedProperties.findIndex(p => p.name === name);

    if (propertyIndex !== -1) {
      updatedProperties[propertyIndex] = { ...updatedProperties[propertyIndex], value };
    } else {
      updatedProperties.push({ id: `piping-${Date.now()}`, name, value });
    }

    const newGeneralSettings = {
      ...generalSettings,
      pipingSystemProperties: updatedProperties
    };

    setGeneralSettings(newGeneralSettings);
    syncGeneralSettingsToStore(newGeneralSettings);
  }, [generalSettings, syncGeneralSettingsToStore]);

  // Handle project property change
  const handleProjectPropertyChange = useCallback((name: string, value: string) => {
    const updatedProperties = [...generalSettings.projectProperties];
    const propertyIndex = updatedProperties.findIndex(p => p.name === name);

    if (propertyIndex !== -1) {
      updatedProperties[propertyIndex] = { ...updatedProperties[propertyIndex], value };
    } else {
      updatedProperties.push({ id: `project-${Date.now()}`, name, value });
    }

    const newGeneralSettings = {
      ...generalSettings,
      projectProperties: updatedProperties
    };

    setGeneralSettings(newGeneralSettings);
    syncGeneralSettingsToStore(newGeneralSettings);
  }, [generalSettings, syncGeneralSettingsToStore]);

  // Function to validate the configuration
  const validateConfiguration = useCallback((): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Validate mapping table attributes
    for (const objectType of generalSettings.mappingTable.objectType) {
      for (const attr of objectType.attributes) {
        if (!attr.pcfAttributeName.trim()) {
          errors.push(`PCF Attribute name is required for attribute with order ${attr.order} in ${objectType.name}`);
        }
      }
    }

    // Validate piping system properties
    for (const prop of generalSettings.pipingSystemProperties) {
      if (!prop.name.trim()) {
        errors.push(`Piping system property name is required for property with ID ${prop.id}`);
      }
    }

    // Validate project properties
    for (const prop of generalSettings.projectProperties) {
      if (!prop.name.trim()) {
        errors.push(`Project property name is required for property with ID ${prop.id}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }, [generalSettings]);

  // Function to save the configuration
  const saveConfiguration = useCallback(async (profileName?: string, settingsType?: SettingsType) => {
    // Validate configuration before saving
    const validation = validateConfiguration();
    if (!validation.isValid) {
      console.error('Configuration validation failed:', validation.errors);
      return Promise.reject(new Error(`Configuration validation failed: ${validation.errors.join(', ')}`));
    }

    try {
      // In real runtime modes, persist to pipeex settings backend first.
      if (isGrpcRuntimeAvailable()) {
        await saveCurrentConfigurationToPipeex(
          profileName || 'PipeEXUI Design',
          settingsType ?? SettingsType.User
        );
      }

      return Promise.resolve();
    } catch (error) {
      console.error('Error saving configuration:', error);
      return Promise.reject(error);
    }
  }, [validateConfiguration]);

  // Get current attributes for selected object type
  // Use Zustand state directly to ensure we get the latest data after migrations
  const currentAttributes = useMemo(() => {
    const objectTypes = generalSettings?.mappingTable?.objectType;
    const selectedAttributes = Array.isArray(objectTypes)
      ? objectTypes.find((obj: ObjectType) => obj && obj.name === selectedObjectType)?.attributes || []
      : [];

    return selectedObjectType === 'ALL'
      ? sanitizeAllObjectTypeAttributes(selectedAttributes)
      : selectedAttributes;
  }, [generalSettings, selectedObjectType]);

  const objectTypeOptions = useMemo(() => {
    const objectTypes = generalSettings?.mappingTable?.objectType;
    return Array.isArray(objectTypes) ? objectTypes : [];
  }, [generalSettings]);

  // Get ALL configuration objects from stores (not individual properties)
  const menuConfig = useConfigurationStore(state => state.menuConfiguration);
  const connectorConfig = useConfigurationStore(state => state.connectorBindingConfiguration);
  const processConfig = useConfigurationStore(state => state.processPropertiesConfiguration);
  const boltConfig = useConfigurationStore(state => state.boltConfiguration);
  const categoryConfig = useConfigurationStore(state => state.additionalCategoriesConfiguration);

  // Build parameter options DYNAMICALLY from configuration stores
  const configurationParameterOptions = useMemo(() => {
    const options: { value: string; label: string; group: string }[] = [];

    // Helper to extract string values from an object recursively
    const extractParameterValues = (obj: any, groupName: string, prefix: string = '') => {
      if (!obj || typeof obj !== 'object') return;

      Object.entries(obj).forEach(([key, val]) => {
        // Skip certain keys that aren't parameter names
        if (key === 'checked' || key === 'Checked' || key.endsWith('Checked')) return;

        // If value is an object with a 'value' property, use that
        if (val && typeof val === 'object' && 'value' in val) {
          const paramValue = (val as any).value;
          if (typeof paramValue === 'string' && paramValue.trim() !== '') {
            // Convert camelCase/PascalCase key to readable label
            const label = key
              .replace(/([A-Z])/g, ' $1')
              .replace(/Value$/, '')
              .trim()
              .toUpperCase()
              .replace(/ /g, '-');

            options.push({
              value: paramValue,
              label: `${label} (from ${groupName})`,
              group: `Previous Pages - ${groupName}`
            });
          }
        }
        // If value is a plain string, use it directly
        else if (typeof val === 'string' && val.trim() !== '') {
          const label = key
            .replace(/([A-Z])/g, ' $1')
            .replace(/Value$/, '')
            .trim()
            .toUpperCase()
            .replace(/ /g, '-');

          options.push({
            value: val,
            label: `${label} (from ${groupName})`,
            group: `Previous Pages - ${groupName}`
          });
        }
      });
    };

    // Extract from all configuration stores
    extractParameterValues(menuConfig, 'Menu');
    extractParameterValues(connectorConfig, 'Connector Binding');
    extractParameterValues(processConfig, 'Process Properties');
    extractParameterValues(boltConfig, 'Bolt Connection');
    extractParameterValues(categoryConfig, 'Categories');

    return options;
  }, [menuConfig, connectorConfig, processConfig, boltConfig, categoryConfig]);

  // Keep this hook local to configuration state only. Mounted mapping editor
  // sections load the live Revit catalog on demand so QuickFill/validation/export
  // tab startup does not queue an unrelated Revit read.
  const revitParameterOptions = configurationParameterOptions;

  return {
    activeTab,
    generalSettings,
    advancedSettings,
    selectedObjectType,
    processConditionsExpanded,
    projectPropertiesExpanded,
    handleTabChange,
    handleObjectTypeChange,
    handleAttributeChange,
    handleAddAttribute,
    handleRemoveAttribute,
    handleAddObjectType,
    handleCenterOfCoordinatesChange,
    handleProcessConditionsChange,
    handleProjectPropertiesChange,
    onProcessConditionsValueChange,
    onProjectPropertiesValueChange,
    onPipingSpecValuePCChange,
    onTracingSpecValuePCChange,
    onAttribute12ValueChange,
    onAttribute13ValueChange,
    onAttribute14ValueChange,
    onAttribute15ValueChange,
    onAttribute16ValueChange,
    onAttribute17ValueChange,
    onAttribute18ValueChange,
    onAttribute19ValueChange,
    onAttribute20ValueChange,
    onAttribute21ValueChange,
    onAttribute22ValueChange,
    onAttribute23ValueChange,
    onProjectIdentifierValueChange,
    onProjectNameValueChange,
    onAreaValueChange,
    onAttribute1ClientNameChange,
    onAttribute2ProjectAddressChange,
    onAttribute3ClientDepartmentChange,
    onAttribute4ClientProjectNumberChange,
    onAttribute5ClientDocumentNumberChange,
    onAttribute6ClientCodeChange,
    onAttribute7EngPartyOfficeChange,
    onAttribute8EngPartyOfficeLocationChange,
    onAttribute9EngPartyProjectNumberChange,
    onAttribute10EngPartyAreaCodeChange,
    handlePipingSystemPropertyChange,
    handleProjectPropertyChange,
    saveConfiguration,
    validateConfiguration,
    currentAttributes,
    objectTypeOptions,
    revitParameterOptions,
    setProcessConditionsExpanded,
    setProjectPropertiesExpanded,
    processConditionsChecked,
    projectPropertiesChecked,
    // Process conditions and project properties values - already wrapped in FormField objects
    processConditionsValue,
    projectPropertiesValue,
    // Process conditions attributes
    pipingSpecValuePC,
    tracingSpecValuePC,
    attribute12Value,
    attribute13Value,
    attribute14Value,
    attribute15Value,
    attribute16Value,
    attribute17Value,
    attribute18Value,
    attribute19Value,
    attribute20Value,
    attribute21Value,
    attribute22Value,
    attribute23Value,
    // Project properties attributes
    projectIdentifierValue,
    projectNameValue,
    areaValue,
    attribute1ClientName,
    attribute2ProjectAddress,
    attribute3ClientDepartment,
    attribute4ClientProjectNumber,
    attribute5ClientDocumentNumber,
    attribute6ClientCode,
    attribute7EngPartyOffice,
    attribute8EngPartyOfficeLocation,
    attribute9EngPartyProjectNumber,
    attribute10EngPartyAreaCode,
    // Add setGeneralSettings to the return object
    setGeneralSettings,
    setAdvancedSettings,
    // General attributes values
    skeyValue: { value: sKeyValue },
    objectTypeNameValue: { value: objectTypeValue },
    pipingSpecValue: { value: pipingSpecValue },
    systemGroupingValue: { value: systemGroupingValue },
    insulationSpecValue,
    tracingSpecValue,
    weightValue,
    insulationThicknessValue,
    // General attribute handlers
    onSkeyValueChange: (value: string) => updateMenuConfiguration({ sKeyValue: value }),
    onObjectTypeNameValueChange: (value: string) => updateMenuConfiguration({ objectTypeValue: value }),
    onPipingSpecValueChange: (value: string) => updateMenuConfiguration({ pipingSpecValue: value }),
    onSystemGroupingValueChange: (value: string) => updateMenuConfiguration({ systemGroupingValue: value }),
    onInsulationSpecValueChange: (value: string) => updateConnectorBindingField('insulationSpecValue', value),
    onTracingSpecValueChange: (value: string) => updateConnectorBindingField('tracingSpecValue', value),
    onWeightValueChange: (value: string) => updateConnectorBindingField('weightValue', value),
    onInsulationThicknessValueChange: (value: string) => updateConnectorBindingField('insulationThicknessValue', value),
    // Accessibility helpers
    getTabAriaProps: (tabIndex: number) => ({
      id: `tab-${tabIndex}`,
      'aria-controls': `tabpanel-${tabIndex}`,
    }),
    getPanelAriaProps: (tabIndex: number) => ({
      id: `tabpanel-${tabIndex}`,
      'aria-labelledby': `tab-${tabIndex}`,
      role: 'tabpanel' as const,
    }),
    getObjectTypeAriaProps: (objectType: string) => ({
      id: `object-type-${objectType}`,
      'aria-label': objectType,
    }),
    getAttributeAriaProps: (attribute: Attribute) => ({
      id: `attribute-${attribute.order}`,
      'aria-label': attribute.pcfAttributeName,
    })
  };
};
